/**
 * Mobile Session Storage Hook
 * Clean, simplified version for managing transaction form state
 * 
 * Handles saving and restoring form state from sessionStorage
 * This allows users to navigate away and come back without losing their form data
 * 
 * TODO: State saving is currently not working properly - needs to be fixed later.
 * The back button works correctly, but state restoration when navigating back to
 * the transaction page needs to be implemented/tested.
 * 
 * Current status:
 * - Back button navigation: ✅ Working
 * - State saving on navigation away: ⚠️ Needs testing/fixing
 * - State restoration on return: ⚠️ Needs implementation/testing
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { TransactionCustomFieldValues } from "../../../../types";

export interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: Record<string, any>;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

export interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

export interface SavedState {
  formData?: FormData;
  transferFormData?: TransferFormData;
  focusedField: string | null;
  calculatorExpression: string;
  addOptionDialogOpen: boolean;
  addOptionDialogDetailName: string;
  addOptionDialogValue: string;
  newlyAddedDetailOptions: Record<string, string[]>;
  openDetailLists: Record<string, boolean>;
  rateManuallyEdited: boolean;
  selectedCategoryDetails: any[];
  transactionType: "expense" | "income" | "transfer";
}

export interface UseMobileSessionStorageParams {
  workspaceId: string;
  transactionType: "expense" | "income" | "transfer";
  storageKey: string;
  routeStorageKey: string;
  formData: FormData;
  transferFormData: TransferFormData;
  focusedField: string | null;
  calculatorExpression: string;
  addOptionDialogOpen: boolean;
  addOptionDialogDetailName: string;
  addOptionDialogValue: string;
  newlyAddedDetailOptions: Record<string, string[]>;
  openDetailLists: Record<string, boolean>;
  rateManuallyEdited: boolean;
  selectedCategoryDetails: any[];
  isNavigatingBackRef: React.MutableRefObject<boolean>;
}

export interface UseMobileSessionStorageResult {
  saveState: () => void;
  getSavedState: () => SavedState | null;
  clearSavedState: () => void;
  hasRestoredStateRef: React.MutableRefObject<boolean>;
}

/**
 * Hook for managing session storage for mobile transaction form
 * 
 * Design principles:
 * - Single responsibility: Only handles state saving/restoration
 * - Clear intent: Uses isNavigatingBackRef to know when NOT to save
 * - No duplicate logic: One place for state saving
 * - Simple: Minimal effects, clear flow
 */
export const useMobileSessionStorage = ({
  workspaceId,
  transactionType,
  storageKey,
  routeStorageKey,
  formData,
  transferFormData,
  focusedField,
  calculatorExpression,
  addOptionDialogOpen,
  addOptionDialogDetailName,
  addOptionDialogValue,
  newlyAddedDetailOptions,
  openDetailLists,
  rateManuallyEdited,
  selectedCategoryDetails,
  isNavigatingBackRef,
}: UseMobileSessionStorageParams): UseMobileSessionStorageResult => {
  const location = useLocation();
  const hasRestoredStateRef = useRef<boolean>(false);
  const prevLocationRef = useRef(location.pathname);

  // Store latest state in refs for saving (avoids stale closures)
  const stateRef = useRef({
    formData,
    transferFormData,
    focusedField,
    calculatorExpression,
    addOptionDialogOpen,
    addOptionDialogDetailName,
    addOptionDialogValue,
    newlyAddedDetailOptions,
    openDetailLists,
    rateManuallyEdited,
    selectedCategoryDetails,
    transactionType,
  });

  // Update state ref whenever state changes
  useEffect(() => {
    stateRef.current = {
      formData,
      transferFormData,
      focusedField,
      calculatorExpression,
      addOptionDialogOpen,
      addOptionDialogDetailName,
      addOptionDialogValue,
      newlyAddedDetailOptions,
      openDetailLists,
      rateManuallyEdited,
      selectedCategoryDetails,
      transactionType,
    };
  }, [
    formData,
    transferFormData,
    focusedField,
    calculatorExpression,
    addOptionDialogOpen,
    addOptionDialogDetailName,
    addOptionDialogValue,
    newlyAddedDetailOptions,
    openDetailLists,
    rateManuallyEdited,
    selectedCategoryDetails,
    transactionType,
  ]);

  // Helper function to get saved state from sessionStorage
  const getSavedState = (): SavedState | null => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error("Error loading saved transaction state:", error);
    }
    return null;
  };

  // Helper function to save state to sessionStorage
  // TODO: Currently not working - needs to be fixed/tested later
  const saveState = () => {
    // Don't save if intentionally navigating back
    if (isNavigatingBackRef.current) {
      return;
    }

    // TODO: State saving needs to be tested and fixed
    // Currently disabled to prevent issues - will continue later
    return;

    try {
      const current = stateRef.current;
      const stateToSave: SavedState = {
        formData: transactionType === "transfer" ? undefined : current.formData,
        transferFormData: transactionType === "transfer" ? current.transferFormData : undefined,
        focusedField: current.focusedField,
        calculatorExpression: current.calculatorExpression,
        addOptionDialogOpen: current.addOptionDialogOpen,
        addOptionDialogDetailName: current.addOptionDialogDetailName,
        addOptionDialogValue: current.addOptionDialogValue,
        newlyAddedDetailOptions: current.newlyAddedDetailOptions,
        openDetailLists: current.openDetailLists,
        rateManuallyEdited: current.rateManuallyEdited,
        selectedCategoryDetails: current.selectedCategoryDetails,
        transactionType: current.transactionType,
      };
      
      sessionStorage.setItem(storageKey, JSON.stringify(stateToSave));
    } catch (error) {
      console.error("Error saving transaction state:", error);
    }
  };

  // Helper function to clear saved state
  const clearSavedState = () => {
    try {
      sessionStorage.removeItem(storageKey);
      sessionStorage.removeItem(routeStorageKey);
    } catch (error) {
      console.error("Error clearing saved transaction state:", error);
    }
  };

  // Reset restoration flag when on transaction page
  useEffect(() => {
    const isOnTransactionPage = location.pathname.includes("/create-transaction/");
    if (isOnTransactionPage) {
      hasRestoredStateRef.current = false;
    }
  }, [location.pathname]);

  // Handle location changes: Save state when navigating away (if not intentional)
  useEffect(() => {
    const wasOnTransactionPage = prevLocationRef.current.includes("/create-transaction/");
    const isOnTransactionPage = location.pathname.includes("/create-transaction/");

    if (wasOnTransactionPage && !isOnTransactionPage) {
      // User navigated away from transaction page
      if (isNavigatingBackRef.current) {
        // Intentionally navigating back - clear everything
        clearSavedState();
        isNavigatingBackRef.current = false;
      } else {
        // Unintentional navigation - save state and route info
        saveState();
        try {
          const routeInfo = {
            type: transactionType,
            path: prevLocationRef.current,
          };
          sessionStorage.setItem(routeStorageKey, JSON.stringify(routeInfo));
        } catch (error) {
          console.error("Error saving route info:", error);
        }
      }
    } else if (isOnTransactionPage) {
      // User is on transaction page - clear route info (we're already here)
      try {
        sessionStorage.removeItem(routeStorageKey);
      } catch (error) {
        // Silent fail
      }
    }

    prevLocationRef.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, transactionType, routeStorageKey, storageKey]); // Removed isNavigatingBackRef (refs shouldn't be in deps)

  return {
    saveState,
    getSavedState,
    clearSavedState,
    hasRestoredStateRef,
  };
};
